/** A thread message's status in Figma's Tag shape (170:4090): Sent and Received take Figma's own fills; every other status keeps its pill colours. */
import type { OutreachMessageStatus } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { SCHEDULED_WAITING_LABEL, STATUS_STYLES } from './OutreachMessageStatusPill';

const FIGMA_FILLS: Partial<Record<OutreachMessageStatus, string>> = {
  sent: 'bg-(--brand-500) text-white',
  received: 'bg-(--stone-100) text-(--foreground-secondary-default)',
  // Muted text on the stone fill is under 4.5:1 at 12px.
  awaiting_approval: 'bg-(--stone-100) text-(--foreground-secondary-default)',
};
const TAG_BASE = 'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[12px]/4 font-medium uppercase';

export interface MessageTagProps {
  status: OutreachMessageStatus;
  /** Extra context (how a reply was matched, a send error) as a tooltip. */
  hint?: string;
  /** Also read the hint to screen readers, where nothing else on the card states it. */
  announceHint?: boolean;
  /** An unsent draft approved to wait for an email: Scheduled, never Draft or Sent, until it really goes out. */
  approvedWaiting?: boolean;
  /** A send the system refused at send time: the failed look, read as Not sent. */
  notSent?: boolean;
}

export function MessageTag({ status, hint, announceHint = false, approvedWaiting = false, notSent = false }: MessageTagProps) {
  const approved = approvedWaiting && status === 'awaiting_approval';
  const style = (approved ? STATUS_STYLES.queued : notSent ? STATUS_STYLES.failed : STATUS_STYLES[status]) as (typeof STATUS_STYLES)[OutreachMessageStatus] | undefined;
  // A status the backend adds later still renders, so one row never takes the thread down.
  if (!style) {
    return (
      <span data-part="tag" title={hint} className={cn(TAG_BASE, 'bg-(--stone-100) text-(--foreground-secondary-default)')}>
        {status}
      </span>
    );
  }
  const { label, cls, icon: Icon } = style;
  return (
    <span data-part="tag" title={hint} className={cn(TAG_BASE, (!approved && !notSent && FIGMA_FILLS[status]) || cls)}>
      <Icon size={12} weight="bold" aria-hidden />
      <span>{approved ? SCHEDULED_WAITING_LABEL : notSent ? 'Not sent' : status === 'received' ? 'Received' : label}</span>
      {hint && announceHint && <span className="sr-only">{`. ${hint}`}</span>}
    </span>
  );
}
